import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/ar';
import 'dayjs/locale/en';
import 'dayjs/locale/fr';
import i18n from '../i18n/config';

dayjs.extend(duration);
dayjs.extend(relativeTime);

export const formatDateTime = (date: string | Date): string => {
  const locale = i18n.language || 'en';
  return dayjs(date).locale(locale).format('DD/MM/YYYY HH:mm');
};

export const formatDate = (date: string | Date): string => {
  const locale = i18n.language || 'en';
  return dayjs(date).locale(locale).format('DD/MM/YYYY');
};

export const formatTime = (date: string | Date): string => {
  const locale = i18n.language || 'en';
  return dayjs(date).locale(locale).format('HH:mm');
};

export const getTimeRemaining = (deadline: string | Date): {
  isOverdue: boolean;
  hours: number;
  minutes: number;
  seconds: number;
  text: string;
  percentage: number;
} => {
  const now = dayjs();
  const deadlineDate = dayjs(deadline);
  const diff = deadlineDate.diff(now);
  const isOverdue = diff < 0;

  const absDiff = Math.abs(diff);
  const hours = Math.floor(absDiff / (1000 * 60 * 60));
  const minutes = Math.floor((absDiff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((absDiff % (1000 * 60)) / 1000);

  const lang = i18n.language || 'en';
  let text = '';

  // Format with padded minutes and seconds for chronometer display
  const paddedMinutes = String(minutes).padStart(2, '0');
  const paddedSeconds = String(seconds).padStart(2, '0');

  if (lang === 'ar') {
    if (isOverdue) {
      text = `متأخر ${hours}:${paddedMinutes}:${paddedSeconds}`;
    } else {
      text = `${hours}:${paddedMinutes}:${paddedSeconds} متبقي`;
    }
  } else if (lang === 'fr') {
    if (isOverdue) {
      text = `En retard de ${hours}:${paddedMinutes}:${paddedSeconds}`;
    } else {
      text = `${hours}:${paddedMinutes}:${paddedSeconds} restant`;
    }
  } else {
    if (isOverdue) {
      text = `Overdue by ${hours}:${paddedMinutes}:${paddedSeconds}`;
    } else {
      text = `${hours}:${paddedMinutes}:${paddedSeconds} remaining`;
    }
  }

  // Calculate percentage for progress bar (assuming 24 hour SLA)
  const totalHours = 24;
  const remainingHours = hours + minutes / 60;
  const percentage = isOverdue ? 0 : Math.max(0, Math.min(100, (remainingHours / totalHours) * 100));

  return { isOverdue, hours, minutes, seconds, text, percentage };
};

export const fromNow = (date: string | Date): string => {
  const locale = i18n.language || 'en';
  return dayjs(date).locale(locale).fromNow();
};

export const isWithinHours = (date: string | Date, hours: number): boolean => {
  const now = dayjs();
  const targetDate = dayjs(date);
  const diff = targetDate.diff(now, 'hour');
  return diff <= hours && diff >= 0;
};

export const getStatusColor = (status: string): string => {
  switch (status) {
    case 'pending':
      return '#ff9800'; // orange
    case 'in_progress':
      return '#2196f3'; // blue
    case 'rework_requested':
      return '#f44336'; // red
    case 'resolved':
      return '#4caf50'; // green
    case 'closed':
      return '#9e9e9e'; // grey
    case 'closed_by_admin':
      return '#7c3aed'; // purple
    default:
      return '#9e9e9e';
  }
};

export const getStatusText = (status: string): string => {
  const lang = i18n.language || 'en';

  const statusTranslations: Record<string, Record<string, string>> = {
    en: {
      pending: 'Pending',
      in_progress: 'In Progress',
      rework_requested: 'Rework Requested',
      resolved: 'Resolved',
      closed: 'Closed',
      closed_by_admin: 'Closed by Admin',
    },
    ar: {
      pending: 'معلقة',
      in_progress: 'قيد التنفيذ',
      rework_requested: 'مطلوب إعادة عمل',
      resolved: 'تم الحل',
      closed: 'مغلقة',
      closed_by_admin: 'مغلقة من الإدارة',
    },
    fr: {
      pending: 'En Attente',
      in_progress: 'En Cours',
      rework_requested: 'Reprise Demandée',
      resolved: 'Résolue',
      closed: 'Fermée',
      closed_by_admin: 'Fermée par Admin',
    },
  };

  return statusTranslations[lang]?.[status] || status;
};
